/**
 * Reading the roster by RSVP.
 *
 * The answer was always stored on a guest; these are the rules that make it
 * mean something. They are pure and live here rather than in the guest list,
 * because the same three questions are asked in three places — the list's
 * filter chips, its counts, and the plan panel's "ready to print?" checklist.
 */

import type { Guest, Rsvp } from './types'

/** What the chips above the guest list can be set to. */
export type RsvpFilter = 'all' | Rsvp

export interface RsvpTally {
  all: number
  yes: number
  pending: number
  no: number
}

/** The number beside each chip. */
export function rsvpTally(guests: Guest[]): RsvpTally {
  const tally: RsvpTally = { all: guests.length, yes: 0, pending: 0, no: 0 }
  for (const guest of guests) tally[guest.rsvp] += 1
  return tally
}

export function matchesRsvp(guest: Guest, filter: RsvpFilter): boolean {
  return filter === 'all' || guest.rsvp === filter
}

/**
 * Whether an unseated guest belongs in the waiting list.
 *
 * Someone who cannot come needs no chair, so a decline stays out of the list
 * and out of "everyone has a chair": the list is a to-do, and they are not on
 * it. The one exception is the planner asking for them — with the "Can't" chip
 * active, the declines are exactly what the list is for.
 */
export function waitsForChair(guest: Guest, filter: RsvpFilter): boolean {
  if (!matchesRsvp(guest, filter)) return false
  return guest.rsvp !== 'no' || filter === 'no'
}

/** How many chairs the plan is still short, declines excluded. */
export function chairsStillNeeded(guests: Guest[], seated: ReadonlySet<string>): number {
  return guests.filter((guest) => guest.rsvp !== 'no' && !seated.has(guest.id)).length
}

/**
 * The ring a chair's double-click walks: coming, then cannot come, then no
 * answer yet, then round again.
 *
 * Fixed and cyclic rather than a toggle, because the three answers are equal
 * citizens — the planner walking a table marks a decline as often as a yes,
 * and every state has to be reachable without leaving the room for the panel.
 */
const RSVP_CYCLE = ['yes', 'no', 'pending'] as const

export function nextRsvp(current: Rsvp): Rsvp {
  return RSVP_CYCLE[(RSVP_CYCLE.indexOf(current) + 1) % RSVP_CYCLE.length]
}
